/**
 * =============================================================================
 *  TIM ANH DA NHUNG TREN MOT TRANG, qua danh sach toan tu cua pdf.js
 * =============================================================================
 *
 *  pdf.js co getOperatorList(): no tra ve dung chuoi lenh ve cua trang, da trai
 *  phang qua cac Form XObject. Ta duyet chuoi do, giu MOT NGAN XEP TRANG THAI DO
 *  HOA y nhu trinh xem lam, va moi lan gap toan tu ve anh thi ghi lai ma tran
 *  dang co hieu luc. Ma tran do chinh la vi tri va kich thuoc cua anh tren trang
 *  - anh trong PDF luon ve trong o vuong don vi, moi thu khac nam trong ma tran.
 *
 * =============================================================================
 *  TAI SAO CHI DEN DAY LA CHUA DU DE SUA
 * =============================================================================
 *  Danh sach toan tu cho ta biet anh O DAU. No KHONG cho ta biet doan byte nao
 *  trong luong noi dung dat no o do - pdf.js khong lo vi tri byte ra. Muon di
 *  chuyen hay xoa thi phai tu doc luong (imageBlocks.ts).
 *
 *  Hai duong doc do phai KHOP NHAU, va do la ca van de. pdf.js dem "lan ve anh"
 *  sau khi da trai phang Form XObject; luong noi dung thi dem toan tu `Do` o cap
 *  cao nhat. Mot trang co Form XObject chua anh se cho hai con so khac nhau, va
 *  neu ta lang le ghep cap theo thu tu thi nguoi dung keo mot anh se lam anh
 *  KHAC dich cho - im lang, tren mot trang ho khong nhin.
 *
 *  Nen o day co mot cua chan tuong minh: hai he dem khong bang nhau thi TAT
 *  chuc nang sua anh cho ca trang do, kem ly do doc duoc. Mot tinh nang bi tat
 *  con nguoi dung con lam cach khac; mot anh bi dich nham thi khong.
 *
 * =============================================================================
 *  DO HOA VECTO NAM NGOAI PHAM VI - CO CHU DICH
 * =============================================================================
 *  Do hoa vecto trong PDF la mot chuoi lien tuc cac toan tu duong (m, l, c) va
 *  toan tu mau, KHONG CO RANH GIOI DOI TUONG. Mot cai logo co the la 200 toan tu
 *  dan xen nhau ma khong co gi danh dau "hinh nay bat dau o day". Gom chung
 *  thanh doi tuong chon duoc chinh la viec ma FPDFPage_GetObject cua PDFium lam,
 *  tuc la can loi Rust.
 *
 *  Tep nay do do chi DEM so lan to/net duong, de giao dien noi that voi nguoi
 *  dung khi ho bam vao mot vung vecto. Khong co ham nao o day gia vo chon duoc
 *  mot hinh vecto.
 */
import type { PdfMatrix, PdfPoint, PdfRect } from '@pdfeditor/types';
import { pdfjsLib, type PDFPageProxy } from '../pdfjs';
import { IDENTITY, mul, unitSquareBounds } from './matrix';

export interface EmbeddedImage {
  /** Ma on dinh trong mot phien: "p{trang}-img{so thu tu Do}". */
  id: string;
  pageIndex: number;
  /** Thu tu trong danh sach toan tu cua pdf.js. Dung de ghep voi luong noi dung. */
  imageIndex: number;
  /** Ma tran DAY DU tai luc ve - da gom moi phep bien doi bao ngoai. */
  fullMatrix: PdfMatrix;
  /** Hop bao tren trang, he toa do PDF. */
  rect: PdfRect;
}

export interface PageImageScan {
  images: EmbeddedImage[];
  /**
   * So lan to hoac di net mot duong. Lon hon 0 nghia la trang co do hoa vecto,
   * va do la thu giao dien phai noi that rang khong sua duoc o ban web.
   */
  vectorPaintCount: number;
}

/**
 * Duyet danh sach toan tu cua mot trang.
 *
 * Dat tien: getOperatorList() phan tich lai ca trang. Ben goi PHAI ghi nho ket
 * qua theo trang - xem bo nho dem trong PdfCoreBrowser.
 */
export async function scanPageForImages(
  page: PDFPageProxy,
  pageIndex: number,
): Promise<PageImageScan> {
  const OPS = pdfjsLib.OPS as unknown as Record<string, number>;
  const list = await page.getOperatorList();

  const images: EmbeddedImage[] = [];
  let vectorPaintCount = 0;

  let ctm: PdfMatrix = IDENTITY;
  const stack: PdfMatrix[] = [];

  const PAINT_IMAGE = new Set(
    [
      OPS['paintImageXObject'],
      OPS['paintImageXObjectRepeat'],
      OPS['paintJpegXObject'],
      OPS['paintImageMaskXObject'],
      OPS['paintImageMaskXObjectRepeat'],
      OPS['paintImageMaskXObjectGroup'],
      OPS['paintInlineImageXObject'],
      OPS['paintInlineImageXObjectGroup'],
    ].filter((v): v is number => typeof v === 'number'),
  );
  const PAINT_PATH = new Set(
    [
      OPS['fill'],
      OPS['eoFill'],
      OPS['stroke'],
      OPS['closeStroke'],
      OPS['fillStroke'],
      OPS['eoFillStroke'],
      OPS['closeFillStroke'],
      OPS['closeEOFillStroke'],
      OPS['constructPath'],
    ].filter((v): v is number => typeof v === 'number'),
  );

  for (let i = 0; i < list.fnArray.length; i++) {
    const fn = list.fnArray[i] as number;
    const args = list.argsArray[i] as unknown[];

    if (fn === OPS['save']) {
      stack.push(ctm);
      continue;
    }
    if (fn === OPS['restore']) {
      ctm = stack.pop() ?? IDENTITY;
      continue;
    }
    if (fn === OPS['transform']) {
      const m = readMatrix(args);
      // `cm` NHAN TRAI vao CTM dang co. Dao thu tu o day la loi khong bao gio lo
      // ra tren mot phep ti le va luon lo ra tren mot phep xoay.
      if (m) ctm = mul(m, ctm);
      continue;
    }

    if (PAINT_IMAGE.has(fn)) {
      const imageIndex = images.length;
      images.push({
        id: `p${pageIndex}-img${imageIndex}`,
        pageIndex,
        imageIndex,
        fullMatrix: ctm,
        rect: unitSquareBounds(ctm),
      });
      continue;
    }

    if (PAINT_PATH.has(fn)) vectorPaintCount++;
  }

  return { images, vectorPaintCount };
}

function readMatrix(args: readonly unknown[]): PdfMatrix | null {
  // pdf.js gui sau doi so roi, hoac mot mang sau phan tu - ca hai deu gap.
  const flat =
    args.length === 1 && Array.isArray(args[0]) ? (args[0] as unknown[]) : (args as unknown[]);
  if (flat.length < 6) return null;
  const nums = flat.slice(0, 6).map((v) => (typeof v === 'number' ? v : Number.NaN));
  if (nums.some((v) => !Number.isFinite(v))) return null;
  return nums as unknown as PdfMatrix;
}

/**
 * Hai he dem co khop nhau khong - xem khoi ghi chu dau tep.
 *
 * `null` la KHOP. Chuoi tra ve la ly do tu choi, hien thang cho nguoi dung.
 */
export function matchesOperatorList(
  imagesFromOperatorList: number,
  imageDrawsInContentStream: number,
  pageIndex: number,
): string | null {
  if (imagesFromOperatorList === imageDrawsInContentStream) return null;
  return (
    `Trang ${pageIndex + 1} có ${imagesFromOperatorList} lượt vẽ ảnh theo trình đọc nhưng ` +
    `${imageDrawsInContentStream} lượt trong luồng nội dung. Chênh lệch này thường do ảnh nằm ` +
    'bên trong một Form XObject lồng nhau. Ghép cặp theo thứ tự lúc này sẽ sửa nhầm ảnh, nên ' +
    'chức năng chọn ảnh bị tắt cho trang này. Cần lõi Rust/PDFium.'
  );
}

/**
 * Anh TREN CUNG tai mot diem.
 *
 * Duyet nguoc: anh ve sau nam tren. Dung dung quy uoc voi pickAt() cua
 * core/annotations/shapes.ts - hai lop chon phai hanh xu giong nhau, neu khong
 * nguoi dung se hoc mot quy tac roi thay no sai o lop kia.
 */
export function pickImageAt<T extends { rect: PdfRect }>(
  images: readonly T[],
  point: PdfPoint,
  tolerancePt = 0,
): T | null {
  for (let i = images.length - 1; i >= 0; i--) {
    const img = images[i] as T;
    const r = img.rect;
    if (
      point.x >= r.x - tolerancePt &&
      point.x <= r.x + r.width + tolerancePt &&
      point.y >= r.y - tolerancePt &&
      point.y <= r.y + r.height + tolerancePt
    ) {
      return img;
    }
  }
  return null;
}

/**
 * Loi giai thich cho mot cu bam KHONG trung anh nao tren mot trang co do hoa
 * vecto. Tra ve null khi khong co gi de noi.
 *
 * Ton tai de giao dien khong im lang: nguoi dung bam vao cai logo, khong co gi
 * xay ra, va ho khong biet la vi ban web khong lam duoc hay vi ho bam truot.
 */
export function vectorClickExplanation(scan: PageImageScan): string | null {
  if (scan.vectorPaintCount === 0) return null;
  return (
    'Chỗ này là đồ họa vector chứ không phải ảnh nhúng. Trong PDF, đồ họa vector là một chuỗi ' +
    'toán tử đường liên tục KHÔNG có ranh giới đối tượng — không có gì đánh dấu hình nào bắt đầu ' +
    'ở đâu — nên bản web không tách ra để chọn được. Việc này cần lõi Rust/PDFium.'
  );
}
